import { useRef, useMemo, useState, useEffect } from 'react'
import { Canvas, useFrame, extend } from '@react-three/fiber'
import { OrbitControls, Environment, Sky, useGLTF, Html } from '@react-three/drei'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
const loadingBarElement = document.querySelector('.loading-bar');

const loadingManager = new THREE.LoadingManager(
    () => {
        window.setTimeout(() => {
            gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 3, value: 0, delay: 1 })

            loadingBarElement.classList.add('ended')
            loadingBarElement.style.transform = ''
        }, 500)
    },

    (itemUrl, itemsLoaded, itemsTotal) => {
        const progressRatio = itemsLoaded / itemsTotal
        loadingBarElement.style.transform = `scaleX(${progressRatio})`
    }
)
const loader = new GLTFLoader(loadingManager);

// Улучшенный шейдер для воды
const waterVertexShader = `
  varying vec2 vUv;
  varying vec3 vPosition;
  varying float vDisplacement;
  uniform float time;
  uniform float waveHeight;
  uniform float waveFrequency;
  uniform float waveSpeed;
  
  void main() {
    vUv = uv;
    vPosition = position;
    
    // Вычисляем расстояние до центра
    float distanceToCenter = length(position.xz);
    
    // Волны, движущиеся к центру
    float wave1 = sin(distanceToCenter * waveFrequency - time * waveSpeed) * waveHeight;
    float wave2 = cos(distanceToCenter * waveFrequency * 0.5 - time * waveSpeed * 1.2) * waveHeight * 0.8;
    
    // Добавляем круговые волны
    float angle = atan(position.z, position.x);
    float wave3 = sin(angle * 5.0 + time * waveSpeed) * waveHeight * 0.5;
    
    vDisplacement = (wave1 + wave2 + wave3) * 0.5;
    vec3 newPosition = position + normal * vDisplacement;
    
    gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
  }
`

const waterFragmentShader = `
  varying vec2 vUv;
  varying vec3 vPosition;
  varying float vDisplacement;
  uniform float time;
  uniform vec3 color;
  uniform float transparency;
  uniform float foamIntensity;
  
  void main() {
    // Усиливаем эффект белых волн
    float foam = smoothstep(0.0, 0.6, vDisplacement) * foamIntensity;
    
    // Создаем базовый цвет воды
    vec3 waterColor = color;
    
    // Добавляем белые гребни волн
    vec3 foamColor = vec3(1.0, 1.0, 1.0);
    float foamMask = smoothstep(0.0, 0.6, vDisplacement);
    vec3 finalColor = mix(waterColor, foamColor, foamMask * foamIntensity);
    
    // Добавляем блики от солнца
    vec3 sunDir = normalize(vec3(1.0, 1.0, 1.0));
    float specular = pow(max(dot(normalize(vPosition), sunDir), 0.0), 32.0) * 0.8;
    finalColor += vec3(specular);
    
    gl_FragColor = vec4(finalColor, transparency);
  }
`

// Шейдер для вулкана
const volcanoVertexShader = `
  varying vec2 vUv;
  varying vec3 vPosition;
  varying float vHeight;
  uniform float time;
  uniform float eruptionIntensity;
  
  void main() {
    vUv = uv;
    vPosition = position;
    vHeight = position.y;
    
    // Убираем эффект дрожания
    vec3 newPosition = position;
    
    gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
  }
`

const volcanoFragmentShader = `
  varying vec2 vUv;
  varying vec3 vPosition;
  varying float vHeight;
  uniform float time;
  uniform float eruptionIntensity;
  uniform vec3 baseColor;
  uniform vec3 lavaColor;
  
  void main() {
    // Базовый цвет вулкана
    vec3 color = baseColor;
    
    // Добавляем эффект лавы
    float lavaGlow = sin(vHeight * 2.0 + time * 3.0) * 0.5 + 0.5;
    float eruptionGlow = smoothstep(0.0, 1.0, vHeight * 0.1) * eruptionIntensity;
    
    // Смешиваем цвета с учетом извержения
    vec3 finalColor = mix(color, lavaColor, lavaGlow * eruptionGlow);
    
    // Добавляем свечение
    float glow = lavaGlow * eruptionGlow * 0.5;
    finalColor += vec3(glow, glow * 0.5, 0.0);
    
    gl_FragColor = vec4(finalColor, 1.0);
  }
`

function UnderwaterRocks() {
    const rocks = useMemo(() => {
        const positions = [];
        const scales = [];
        const rotations = [];

        for (let i = 0; i < 50; i++) {
            // Случайная позиция в радиусе 200 единиц
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.random() * 200;
            const x = Math.cos(angle) * radius;
            const z = Math.sin(angle) * radius;
            const y = -5 - Math.random() * 2; // Под водой

            positions.push([x, y, z]);
            scales.push([Math.random() * 2 + 1, Math.random() * 2 + 1, Math.random() * 2 + 1]);
            rotations.push([Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI]);
        }

        return { positions, scales, rotations };
    }, []);

    return (
        <group>
            {rocks.positions.map((pos, i) => (
                <mesh
                    key={i}
                    position={pos}
                    scale={rocks.scales[i]}
                    rotation={rocks.rotations[i]}
                >
                    <dodecahedronGeometry args={[1, 0]} />
                    <meshStandardMaterial
                        color={0x808080}
                        roughness={0.8}
                        metalness={0.2}
                    />
                </mesh>
            ))}
        </group>
    );
}

function Ocean({ weather }) {
    const waterRef = useRef();
    const uniforms = useMemo(() => ({
        time: { value: 0 },
        color: { value: new THREE.Color(0x0077be) },
        waveHeight: { value: weather === 'windy' ? 0.8 : 0.3 },
        waveFrequency: { value: weather === 'windy' ? 0.25 : 0.15 },
        waveSpeed: { value: weather === 'windy' ? 1.2 : 0.8 },
        transparency: { value: 0.7 },
        foamIntensity: { value: weather === 'windy' ? 0.4 : 0.2 },
        randomness: { value: 0.4 },
        weather: { value: weather === 'windy' ? 1.0 : 0.0 }
    }), [weather]);

    useFrame((state) => {
        uniforms.time.value = state.clock.getElapsedTime();
        // Обновляем параметры волн в зависимости от погоды
        uniforms.waveHeight.value = weather === 'windy' ? 0.8 : 0.3;
        uniforms.waveFrequency.value = weather === 'windy' ? 0.25 : 0.15;
        uniforms.waveSpeed.value = weather === 'windy' ? 1.2 : 0.8;
        uniforms.foamIntensity.value = weather === 'windy' ? 0.4 : 0.2;
        uniforms.weather.value = weather === 'windy' ? 1.0 : 0.0;
    });

    return (
        <>
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -5, 0]}>
                <planeGeometry args={[500, 500, 128, 128]} />
                <shaderMaterial
                    key={`water-shader-${weather}`}
                    vertexShader={waterVertexShader}
                    fragmentShader={waterFragmentShader}
                    uniforms={uniforms}
                    transparent
                    side={THREE.DoubleSide}
                />
            </mesh>
            <UnderwaterRocks />
            <Boat
                weather={weather}
            />
        </>
    );
}

function LavaParticles() {
    const particlesRef = useRef();
    const positionsRef = useRef();
    const velocitiesRef = useRef();

    const particles = useMemo(() => {
        const count = 2000;
        const positions = new Float32Array(count * 3);
        const velocities = new Float32Array(count * 3);
        const sizes = new Float32Array(count);

        for (let i = 0; i < count; i++) {
            // Начальная позиция в кратере вулкана (смещена вправо)
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.random() * 3;
            positions[i * 3] = Math.cos(angle) * radius + 20; // Смещение вправо
            positions[i * 3 + 1] = Math.random() * 10;
            positions[i * 3 + 2] = Math.sin(angle) * radius;

            // Начальная скорость (увеличена вертикальная составляющая)
            velocities[i * 3] = (Math.random() - 0.5) * 0.3;
            velocities[i * 3 + 1] = Math.random() * 2.0 + 1.5; // Увеличена скорость подъема
            velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.3;

            sizes[i] = Math.random() * 2.0 + 2.0; // Увеличен размер частиц
        }

        positionsRef.current = positions;
        velocitiesRef.current = velocities;

        return { positions, velocities, sizes };
    }, []);

    const uniforms = useMemo(() => ({
        time: { value: 0 },
        eruptionIntensity: { value: 0 }
    }), []);

    useFrame((state) => {
        uniforms.time.value = state.clock.getElapsedTime();
        uniforms.eruptionIntensity.value = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.5 + 0.5;

        if (!particlesRef.current || !positionsRef.current || !velocitiesRef.current) return;

        const positions = positionsRef.current;
        const velocities = velocitiesRef.current;

        const spawnWidth = 22; // Ширина области спавна по оси X
        const spawnDepth = 22; // Ширина области спавна по оси Z

        for (let i = 0; i < positions.length; i += 3) {
            positions[i] += velocities[i] * 0.1;
            positions[i + 1] += velocities[i + 1] * 0.1;
            positions[i + 2] += velocities[i + 2] * 0.1;

            // Добавляем вихревой эффект
            const centerX = 20;
            const centerZ = 0;
            const centerY = 50; // Поднимаем центр вихря выше
            const dx = positions[i] - centerX;
            const dy = positions[i + 1] - centerY;
            const dz = positions[i + 2] - centerZ;
            const vortexDistance = Math.sqrt(dx * dx + dz * dz);

            if (vortexDistance > 0.1 && positions[i + 1] > 30) { // Вихрь начинается выше
                const angle = Math.atan2(dz, dx);
                const newAngle = angle + 0.05;
                const speed = 0.1;

                velocities[i] += (Math.cos(newAngle) - dx / vortexDistance) * speed;
                velocities[i + 1] += (Math.random() - 0.5) * 0.05; // Добавляем вертикальное движение
                velocities[i + 2] += (Math.sin(newAngle) - dz / vortexDistance) * speed;
            }

            if (positions[i + 1] > 200) {
                positions[i] = Math.random() * spawnWidth - spawnWidth / 10;
                positions[i + 1] = Math.random() * 10;
                positions[i + 2] = Math.random() * spawnDepth - spawnDepth / 10;

                velocities[i] = (Math.random() - 0.5) * 0.3;
                velocities[i + 1] = Math.random() * 2.0 + 1.5;
                velocities[i + 2] = (Math.random() - 0.5) * 0.3;
            }
        }

        particlesRef.current.geometry.attributes.position.needsUpdate = true;
    });

    return (
        <points ref={particlesRef} renderOrder={2}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={particles.positions.length / 3}
                    array={particles.positions}
                    itemSize={3}
                />
                <bufferAttribute
                    attach="attributes-size"
                    count={particles.sizes.length}
                    array={particles.sizes}
                    itemSize={1}
                />
            </bufferGeometry>
            <shaderMaterial
                uniforms={uniforms}
                vertexShader={`
                    attribute float size;
                    varying vec3 vColor;
                    varying float vHeight;
                    uniform float time;
                    uniform float eruptionIntensity;
                    
                    void main() {
                        vColor = vec3(1.0, 0.3, 0.0) * eruptionIntensity;
                        vHeight = position.y;
                        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                        
                        // Увеличиваем размер частиц с высотой
                        float heightScale = 1.0 + smoothstep(0.0, 200.0, position.y) * 2.0;
                        gl_PointSize = size * heightScale * (600.0 / -mvPosition.z);
                        
                        gl_Position = projectionMatrix * mvPosition;
                    }
                `}
                fragmentShader={`
                    varying vec3 vColor;
                    varying float vHeight;
                    
                    void main() {
                        float r = distance(gl_PointCoord, vec2(0.5));
                        if (r > 0.5) discard;
                        
                        // Увеличиваем толщину черной оконтовки с высотой
                        float outlineWidth = 0.1 + smoothstep(0.0, 200.0, vHeight) * 0.3;
                        float outline = smoothstep(0.5 - outlineWidth, 0.5, r);
                        
                        // Смешиваем цвет с черной оконтовкой
                        vec3 finalColor = mix(vColor, vec3(0.0), outline);
                        float alpha = 1.0 - smoothstep(0.3, 0.5, r);
                        
                        gl_FragColor = vec4(finalColor, alpha);
                    }
                `}
                transparent
                depthWrite={false}
                depthTest={true}
                blending={THREE.AdditiveBlending}
            />
        </points>
    );
}

function SmokeParticles() {
    const smokeRef = useRef();
    const positionsRef = useRef();
    const velocitiesRef = useRef();
    const sizesRef = useRef();
    const opacitiesRef = useRef();
    const rotationsRef = useRef();

    const particles = useMemo(() => {
        const count = 200;
        const positions = new Float32Array(count * 3);
        const velocities = new Float32Array(count * 3);
        const sizes = new Float32Array(count);
        const opacities = new Float32Array(count);
        const rotations = new Float32Array(count);

        const spawnWidth = 22;
        const spawnDepth = 22;

        for (let i = 0; i < count; i++) {
            positions[i * 3] = Math.random() * spawnWidth - spawnWidth / 10;
            positions[i * 3 + 1] = Math.random() * 10;
            positions[i * 3 + 2] = Math.random() * spawnDepth - spawnDepth / 10;

            velocities[i * 3] = (Math.random() - 0.5) * 0.05;
            velocities[i * 3 + 1] = Math.random() * 0.5 + 0.3;
            velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.05;

            sizes[i] = Math.random() * 5.0 + 4.0; // Увеличен размер частиц дыма
            opacities[i] = Math.random() * 0.3 + 0.7;
            rotations[i] = Math.random() * Math.PI * 2;
        }

        positionsRef.current = positions;
        velocitiesRef.current = velocities;
        sizesRef.current = sizes;
        opacitiesRef.current = opacities;
        rotationsRef.current = rotations;

        return { positions, velocities, sizes, opacities, rotations };
    }, []);

    const uniforms = useMemo(() => ({
        time: { value: 0 },
        eruptionIntensity: { value: 0 }
    }), []);

    useFrame((state) => {
        uniforms.time.value = state.clock.getElapsedTime();
        uniforms.eruptionIntensity.value = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.5 + 0.5;

        if (!smokeRef.current || !positionsRef.current || !velocitiesRef.current ||
            !sizesRef.current || !opacitiesRef.current || !rotationsRef.current) return;

        const positions = positionsRef.current;
        const velocities = velocitiesRef.current;
        const sizes = sizesRef.current;
        const opacities = opacitiesRef.current;
        const rotations = rotationsRef.current;

        const spawnWidth = 32; // Ширина области спавна по оси X
        const spawnDepth = 28; // Ширина области спавна по оси Z

        for (let i = 0; i < positions.length; i += 3) {
            positions[i] += velocities[i] * 0.1;
            positions[i + 1] += velocities[i + 1] * 0.1;
            positions[i + 2] += velocities[i + 2] * 0.1;

            // Добавляем вихревой эффект
            const centerX = 20;
            const centerZ = 0;
            const centerY = 50; // Поднимаем центр вихря выше
            const dx = positions[i] - centerX;
            const dy = positions[i + 1] - centerY;
            const dz = positions[i + 2] - centerZ;
            const vortexDistance = Math.sqrt(dx * dx + dz * dz);

            if (vortexDistance > 0.1 && positions[i + 1] > 30) { // Вихрь начинается выше
                const angle = Math.atan2(dz, dx);
                const newAngle = angle + 0.03;
                const speed = 0.05;

                velocities[i] += (Math.cos(newAngle) - dx / vortexDistance) * speed;
                velocities[i + 1] += (Math.random() - 0.5) * 0.05; // Добавляем вертикальное движение
                velocities[i + 2] += (Math.sin(newAngle) - dz / vortexDistance) * speed;
            }

            const height = positions[i + 1];
            sizes[i / 3] = Math.min(15.0, 2.0 + height * 0.1);
            opacities[i / 3] = Math.max(0.0, 1.0 - height * 0.002);
            rotations[i / 3] += 0.01;

            if (positions[i + 1] > 200 || opacities[i / 3] <= 0) {
                positions[i] = Math.random() * spawnWidth - spawnWidth / 10;
                positions[i + 1] = Math.random() * 10;
                positions[i + 2] = Math.random() * spawnDepth - spawnDepth / 10;

                velocities[i] = (Math.random() - 0.5) * 0.05;
                velocities[i + 1] = Math.random() * 0.5 + 0.3;
                velocities[i + 2] = (Math.random() - 0.5) * 0.05;

                sizes[i / 3] = Math.random() * 3.0 + 2.0;
                opacities[i / 3] = Math.random() * 0.3 + 0.7;
                rotations[i / 3] = Math.random() * Math.PI * 2;
            }
        }

        smokeRef.current.geometry.attributes.position.needsUpdate = true;
        smokeRef.current.geometry.attributes.size.needsUpdate = true;
        smokeRef.current.geometry.attributes.opacity.needsUpdate = true;
    });

    return (
        <points ref={smokeRef} renderOrder={2}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={particles.positions.length / 3}
                    array={particles.positions}
                    itemSize={3}
                />
                <bufferAttribute
                    attach="attributes-size"
                    count={particles.sizes.length}
                    array={particles.sizes}
                    itemSize={1}
                />
                <bufferAttribute
                    attach="attributes-opacity"
                    count={particles.opacities.length}
                    array={particles.opacities}
                    itemSize={1}
                />
            </bufferGeometry>
            <shaderMaterial
                uniforms={uniforms}
                vertexShader={`
                    attribute float size;
                    attribute float opacity;
                    varying float vOpacity;
                    varying vec3 vColor;
                    uniform float time;
                    uniform float eruptionIntensity;
                    
                    void main() {
                        vOpacity = opacity;
                        vColor = vec3(0.4, 0.4, 0.4) * eruptionIntensity;
                        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                        gl_PointSize = size * (800.0 / -mvPosition.z);
                        gl_Position = projectionMatrix * mvPosition;
                    }
                `}
                fragmentShader={`
                    varying float vOpacity;
                    varying vec3 vColor;
                    
                    void main() {
                        float r = distance(gl_PointCoord, vec2(0.5));
                        if (r > 0.5) discard;
                        float alpha = vOpacity * (1.0 - smoothstep(0.3, 0.5, r));
                        gl_FragColor = vec4(vColor, alpha);
                    }
                `}
                transparent
                depthWrite={false}
                depthTest={true}
                blending={THREE.AdditiveBlending}
            />
        </points>
    );
}
function MagmaPlane() {
    const planeRef = useRef();
    const uniforms = useMemo(() => ({
        time: { value: 0 },
        eruptionIntensity: { value: 0 },
        bubbleCount: { value: 55 }, // Общее количество частиц
        bubblePositions: { value: new Float32Array(55 * 3) },
        bubbleSpeeds: { value: new Float32Array(55 * 3) },
        bubbleSizes: { value: new Float32Array(55) },
        bubbleTimers: { value: new Float32Array(330) } // Для нелинейного движения
    }), []);

    // Инициализация пузырьков
    useEffect(() => {
        const positions = uniforms.bubblePositions.value;
        const speeds = uniforms.bubbleSpeeds.value;
        const sizes = uniforms.bubbleSizes.value;
        const timers = uniforms.bubbleTimers.value;

        let index = 0; // Индекс для отслеживания текущей частицы
        const totalParticles = 55; // Общее количество частиц
        const interval = 1700; // Увеличьте интервал до 500 миллисекунд

        const addParticle = () => {
            if (index < totalParticles) {
                // Начальные позиции - все пузырьки внизу
                positions[index * 3] = (Math.random() - 0.5) * 40; // x (широкий диапазон)
                positions[index * 3 + 1] = Math.random() * 2; // y (от 0 до 2)
                positions[index * 3 + 2] = (Math.random() - 0.5) * 40; // z (широкий диапазон)

                // Случайные скорости (меньше по X и Z)
                speeds[index * 3] = (Math.random() - 0.5) * 0.0002; // speed x
                speeds[index * 3 + 1] = Math.random() * 0.05 + 0.002; // speed y (вверх)
                speeds[index * 3 + 2] = (Math.random() - 0.5) * 0.0002; // speed z

                // Случайные размеры
                sizes[index] = Math.random() * 1.5 + 2.65;

                // Случайные таймеры для нелинейного движения
                timers[index] = Math.random() * 700.0;

                index++; // Увеличиваем индекс для следующей частицы
            } else {
                clearInterval(intervalId); // Очищаем интервал, если все частицы добавлены
            }
        };

        const intervalId = setInterval(addParticle, interval); // Запускаем интервал

        return () => clearInterval(intervalId); // Очищаем интервал при размонтировании компонента
    }, [uniforms]);

    useFrame((state) => {
        uniforms.time.value = state.clock.getElapsedTime();
        uniforms.eruptionIntensity.value = Math.sin(state.clock.getElapsedTime() * 0.5) * 0.5 + 0.5;

        // Обновление позиций пузырьков
        const positions = uniforms.bubblePositions.value;
        const speeds = uniforms.bubbleSpeeds.value;

        for (let i = 0; i < 55; i++) { // Измените на 55, чтобы соответствовать количеству частиц
            positions[i * 3] += speeds[i * 3]; // Обновление x
            positions[i * 3 + 1] += speeds[i * 3 + 1]; // Обновление y
            positions[i * 3 + 2] += speeds[i * 3 + 2]; // Обновление z

            // Если пузырек выходит за пределы, перемещаем его обратно
            if (positions[i *  3 + 1] > 2) {
                positions[i * 3 + 1] = 0; // Сброс высоты
                positions[i * 3] = (Math.random() - 0.5) * 20; // Случайная x
                positions[i * 3 + 2] = (Math.random() - 0.5) * 20; // Случайная z
            }
        }
    });

    return (
        <mesh ref={planeRef} position={[12.9, 31, 4]} rotation={[0, 0, 0]} scale={[1, 1, 1]}>
            <cylinderGeometry args={[19.6, 18, 2, 32]} />
            <shaderMaterial
                uniforms={uniforms}
                vertexShader={`
                    varying vec2 vUv;
                    varying vec3 vPosition;
                    uniform float time;
                    uniform float eruptionIntensity;
                    uniform int bubbleCount;
                    uniform vec3 bubblePositions[55]; // Обновлено на 55
                    uniform float bubbleSizes[55]; // Обновлено на 55

                    void main() {
                        vUv = uv;
                        vPosition = position;

                        // Базовая анимация поверхности
                        float wave = sin(position.x * 0.5 + time * 1.5) * 0.2 + 
                                    cos(position.z * 0.5 + time * 1.2) * 0.2;

                        // Эффект выплескивания
                        float eruptionWave = sin(time * 4.0 + position.x * 3.0) * eruptionIntensity;
                        float eruptionHeight = smoothstep(0.5, 1.0, length(position.xz / 15.0)) * eruptionWave * 1.5;

                        vec3 newPosition = position;
                        newPosition.y += wave * 0.3 + eruptionHeight;

                        // Добавляем эффект пузырьков
                        for (int i = 0; i < bubbleCount; i++) {
                            vec3 bubblePos = bubblePositions[i];
                            float bubbleSize = bubbleSizes[i];
                            float distance = length(position.xz - bubblePos.xz);
                            float influence = max(0.0, 1.0 - distance / (1.0 * bubbleSize));
                            
                            if (influence > 0.0) {
                                // Эффект подъема с учетом размера пузырька
                                float bubbleEffect = influence * influence * 0.3 * bubbleSize;
                                newPosition.y += bubbleEffect * smoothstep(-2.0, 2.0, bubblePos.y);
                                
                                // Легкое смещение в сторону от пузырька
                                vec2 dir = normalize(position.xz - bubblePos.xz);
                                newPosition.xz += dir * influence * 0.1 * bubbleSize;
                            }
                        }

                        gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
                    }
                `}
                fragmentShader={`
                    varying vec2 vUv;
                    varying vec3 vPosition;
                    uniform float time;
                    uniform float eruptionIntensity;
                    uniform int bubbleCount;
                    uniform vec3 bubblePositions[55]; // Обновлено на 55
                    uniform float bubbleSizes[55]; // Обновлено на 55

                    void main() {
                        // Основной цвет магмы
                        vec3 magmaColor = vec3(1.0, 0.3, 0.1);
                        
                        // Добавляем шум для текстуры
                        float noise1 = sin(vPosition.x * 0.5 + time) * 0.5 + 0.5;
                        float noise2 = cos(vPosition.z * 0.5 + time * 0.8) * 0.5 + 0.5;
                        float noise3 = sin(vPosition.x * 0.3 + vPosition.z * 0.3 + time * 0.3) * 0.5 + 0.5;
                        
                        // Вариации цвета
                        float colorVariation = noise1 * 0.3 + noise2 * 0.2 + noise3 * 0.2;
                        magmaColor += vec3(colorVariation * 0.5, colorVariation * 0.2, 0.0);
                        
                        // Свечение
                        float glow = smoothstep(0.4, 0.8, noise1 * noise2) * 0.8;
                        magmaColor += vec3(glow, glow * 0.3, 0.0);
                        
                        // Эффект пузырьков на поверхности
                        for (int i = 0; i < bubbleCount; i++) {
                            vec3 bubblePos = bubblePositions[i];
                            float bubbleSize = bubbleSizes[i];
                            float distance = length(vPosition - bubblePos);
                            
                            if (distance < bubbleSize * 0.5) {
                                // Темные края пузырьков
                                float edge = smoothstep(0.0, bubbleSize * 0.5, distance);
                                magmaColor = mix(vec3(0.1, 0.05, 0.0), magmaColor, edge);
                                
                                // Светлый центр
                                if (distance < bubbleSize * 0.3) {
                                    float center = 1.0 - distance / (bubbleSize * 0.3);
                                    magmaColor += vec3(center * 0.3, center * 0.15, 0.0);
                                }
                            }
                        }
                        
                        // Эффект извержения
                        magmaColor *= 1.0 + eruptionIntensity * 0.8;
                        
                        // Пульсация
                        float pulse = sin(time * 3.0) * 0.1 + 0.9;
                        magmaColor *= pulse;
                        
                        gl_FragColor = vec4(magmaColor, 1.0);
                    }
                `}
                side={THREE.DoubleSide}
            />
        </mesh>
    );
}

function VolcanoModel() {
    const { scene } = useGLTF('/models/VOLCANO.glb', undefined, (error) => {
        console.error('Error loading VOLCANO model:', error);
    });
    const uniforms = useMemo(() => ({
        time: { value: 0 },
        eruptionIntensity: { value: 0 },
        baseColor: { value: new THREE.Color(0x333333) },
        lavaColor: { value: new THREE.Color(0xff4500) },
        weather: { value: 0.0 }
    }), []);

    useFrame((state) => {
        uniforms.time.value = state.clock.getElapsedTime();
        uniforms.eruptionIntensity.value = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.5 + 0.5;
    });

    useEffect(() => {
        scene.traverse((child) => {
            if (child.isMesh && child.material.map) {
                child.material.map.colorSpace = THREE.SRGBColorSpace;
            }
        });
    }, [scene]);

    return (
        <group>
            <primitive
                object={scene}
                scale={2}
                position={[0, -10, 0]}
                rotation={[0, Math.PI, 0]}
            >
                <shaderMaterial
                    vertexShader={volcanoVertexShader}
                    fragmentShader={volcanoFragmentShader}
                    uniforms={uniforms}
                />
            </primitive>
            <MagmaPlane />
            <LavaParticles />
            <SmokeParticles />
        </group>
    );
}

function TralaleloModel() {
    const { scene } = useGLTF('/models/tralalelo.glb', undefined, (error) => {
        console.error('Error loading Tralalelo model:', error);
    });

    useEffect(() => {
        scene.traverse((child) => {
            if (child.isMesh && child.material.map) {
                child.material.map.colorSpace = THREE.SRGBColorSpace;
            }
        });
    }, [scene]);

    // Создаем материал с текстурой
    const material = useMemo(() => {
        const textureLoader = new THREE.TextureLoader();
        const texture = textureLoader.load('/textures/Tralala_Base_color.png', (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.flipY = false;
            texture.colorSpace = THREE.SRGBColorSpace;
        }, undefined, (error) => {
            console.error('Error loading texture:', error);
        });

        return new THREE.MeshStandardMaterial({
            map: texture,
        });
    }, []);

    // Применяем материал и настраиваем UV-координаты
    useEffect(() => {
        scene.traverse((child) => {
            if (child.isMesh) {
                // Проверяем наличие UV-координат
                if (!child.geometry.attributes.uv) {
                    // Создаем UV-координаты, если их нет
                    const geometry = child.geometry
                    const vertices = geometry.attributes.position.array
                    const uvs = new Float32Array(vertices.length / 3 * 2)

                    // Создаем простую UV-развертку
                    for (let i = 0; i < vertices.length / 3; i++) {
                        const x = vertices[i * 3]
                        const y = vertices[i * 3 + 1]
                        const z = vertices[i * 3 + 2]

                        // Проекция на плоскость
                        uvs[i * 2] = (x + 1) / 2
                        uvs[i * 2 + 1] = (y + 1) / 2
                    }

                    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
                }

                child.material = material
            }
        })
    }, [scene, material])

    return (
        <primitive
            object={scene}
            scale={[2, 2, 2]}
            position={[-55, 5.5, 31]}
            rotation={[-0.2, -Math.PI / 3.3, -0.2]}
        />
    )
}

function Boat({ weather }) {
    const { scene } = useGLTF('/models/bow.glb', undefined, (error) => {
        console.error('Error loading boat model:', error);
    });
    const boatRef = useRef();

    useEffect(() => {
        scene.traverse((child) => {
            if (child.isMesh && child.material.map) {
                child.material.map.colorSpace = THREE.SRGBColorSpace;
            }
        });
    }, [scene]);

    const material = useMemo(() => {
        const texture = new THREE.TextureLoader().load('/textures/row_boat_BaseColor.png', (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.flipY = false;
            texture.colorSpace = THREE.SRGBColorSpace;
        }, undefined, (error) => {
            console.error('Error loading texture:', error);
        });

        return new THREE.MeshStandardMaterial({
            map: texture,
        });
    }, []);

    useFrame((state) => {
        if (boatRef.current) {
            const time = state.clock.getElapsedTime();
            
            // Настраиваем параметры волн в зависимости от погоды
            const waveHeight = weather === 'windy' ? 0.4 : 0.3;
            const waveFrequency = weather === 'windy' ? 0.4 : 0.3; // Увеличиваем частоту для более частых колебаний
            
            // Основная волна
            const wave1 = Math.sin(time * waveFrequency) * waveHeight;
            const wave2 = Math.sin(time * waveFrequency * 1.5 + Math.PI / 2) * (waveHeight * 0.5);
            const wave3 = Math.sin(time * waveFrequency * 0.7) * (waveHeight * 0.3);
            
            const waveHeightValue = (wave1 + wave2 + wave3) * 0.7;
            boatRef.current.position.y = -6.8 + waveHeightValue;
            
            // Наклон лодки по оси Z
            const tiltIntensity = weather === 'windy' ? 0.4 : 0.2; // Увеличиваем интенсивность наклона
            boatRef.current.rotation.z = Math.cos(time * waveFrequency * 1.5) * (waveHeight * tiltIntensity); // Увеличиваем частоту
            
            // Наклон лодки по оси X
            const pitchIntensity = weather === 'windy' ? 0.3 : 0.15; // Увеличиваем интенсивность наклона
            boatRef.current.rotation.x = Math.sin(time * waveFrequency * 1.5) * (waveHeight * pitchIntensity); // Увеличиваем частоту
            
            // Вращение по оси Y при ветре
            if (weather === 'windy') {
                boatRef.current.rotation.y = Math.PI / 2 + Math.sin(time * waveFrequency * 0.8) * 0.5; // Увеличиваем частоту
            }
        }
    });

    return (
        <primitive
            ref={boatRef}
            object={scene}
            scale={[1, 1, 1]}
            position={[20, -6.5, 80]}
            rotation={[0, Math.PI / 2, 0]}
        />
    );
}
function WeatherControl({ onWeatherChange, onVolumeChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const [weather, setWeather] = useState('clear');
    const [volume, setVolume] = useState(0.5);

    const handleWeatherChange = (newWeather) => {
        setWeather(newWeather);
        onWeatherChange(newWeather);
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        onVolumeChange(newVolume);
    };

    const weatherIcons = {
        clear: '☀️',
        storm: '⛈️',
        windy: '💨'
    };

    return (
        <Html
            position={[0, 0, 0]}
            center
            style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 1000,
                pointerEvents: 'auto',
                transform: 'none'
            }}
        >
            <div style={{
                position: 'relative',
                display: 'inline-block'
            }}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        padding: 'clamp(8px, 1vw, 12px)',
                        background: 'rgba(255, 255, 255, 0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: 'clamp(20px, 2vw, 24px)',
                        width: 'clamp(40px, 5vw, 50px)',
                        height: 'clamp(40px, 5vw, 50px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                        transition: 'all 0.3s ease',
                        ':hover': {
                            transform: 'scale(1.1)'
                        }
                    }}
                >
                    {weatherIcons[weather]}
                </button>
                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: 'clamp(10px, 1.5vw, 20px)',
                        borderRadius: '10px',
                        marginTop: '10px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 1000
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 'clamp(5px, 1vw, 10px)',
                            alignItems: 'center'
                        }}>
                            {Object.entries(weatherIcons).map(([type, icon]) => (
                                <button
                                    key={type}
                                    onClick={() => handleWeatherChange(type)}
                                    style={{
                                        padding: 'clamp(8px, 1vw, 12px)',
                                        background: weather === type ? '#4CAF50' : '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: 'clamp(18px, 2vw, 24px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 'clamp(40px, 5vw, 50px)',
                                        height: 'clamp(40px, 5vw, 50px)',
                                        transition: 'all 0.3s ease',
                                        ':hover': {
                                            transform: 'scale(1.1)'
                                        }
                                    }}
                                >
                                    {icon}
                                </button>
                            ))}
                        </div>
                        <div style={{
                            marginTop: '10px',
                            padding: '10px',
                            background: 'rgba(0, 0, 0, 0.05)',
                            borderRadius: '5px'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}>
                                <span style={{ fontSize: '20px' }}>🔊</span>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={volume}
                                    onChange={handleVolumeChange}
                                    style={{
                                        width: '100px',
                                        cursor: 'pointer'
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Html>
    );
}

function Clouds({ weather }) {
    const cloudsRef = useRef();
    const uniforms = useMemo(() => ({
        time: { value: 0 },
        weather: { value: weather === 'storm' ? 1.0 : weather === 'windy' ? 0.5 : 0.0 },
        lightDirection: { value: new THREE.Vector3(1, 1, 1).normalize() }
    }), [weather]);

    useFrame((state) => {
        uniforms.time.value = state.clock.getElapsedTime();
        if (cloudsRef.current) {
            const rotationSpeed = weather === 'windy' ? 0.002 : weather === 'storm' ? 0.001 : 0.0005;
            cloudsRef.current.rotation.y += rotationSpeed;
        }
    });

    if (weather === 'clear') return null;

    // Увеличиваем количество облаков при ветре
    const cloudCount = weather === 'windy' ? 40 : 20;

    return (
        <group ref={cloudsRef}>
            {[...Array(cloudCount)].map((_, i) => (
                <mesh
                    key={i}
                    position={[
                        Math.sin(i) * 100 + Math.random() * 50,
                        50 + Math.random() * 30,
                        Math.cos(i) * 100 + Math.random() * 50
                    ]}
                    scale={[10 + Math.random() * 5, 2 + Math.random() * 2, 10 + Math.random() * 5]}
                >
                    <sphereGeometry args={[1, 32, 32]} />
                    <shaderMaterial
                        uniforms={uniforms}
                        vertexShader={`
                            varying vec2 vUv;
                            varying float vWeather;
                            varying vec3 vNormal;
                            varying vec3 vPosition;
                            uniform float time;
                            uniform float weather;
                            
                            void main() {
                                vUv = uv;
                                vWeather = weather;
                                vNormal = normal;
                                vPosition = position;
                                
                                float pulseIntensity = weather > 0.8 ? 2.0 : weather > 0.3 ? 1.5 : 1.0;
                                float pulse = sin(time + position.x * 0.1) * 0.1 * pulseIntensity;
                                vec3 newPosition = position + normal * pulse;
                                
                                gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
                            }
                        `}
                        fragmentShader={`
                            varying vec2 vUv;
                            varying float vWeather;
                            varying vec3 vNormal;
                            varying vec3 vPosition;
                            uniform float time;
                            uniform vec3 lightDirection;
                            
                            void main() {
                                vec3 cloudColor = vec3(0.9, 0.9, 0.9);
                                
                                if (vWeather > 0.8) {
                                    // Эффект молнии
                                    float lightning = step(0.99, fract(time * 0.2));
                                    float lightningIntensity = lightning * 1.0;
                                    
                                    // Добавляем вспышку молнии
                                    float flash = smoothstep(0.99, 1.0, fract(time * 0.2));
                                    float thunder = sin(time * 10.0) * 0.5 + 0.5;
                                    
                                    // Эффект грозы
                                    float stormEffect = thunder * flash * 0.5;
                                    cloudColor = mix(cloudColor, vec3(1.0, 1.0, 0.8), lightningIntensity + stormEffect);
                                    
                                    // Затемнение для шторма
                                    cloudColor = mix(cloudColor, vec3(0.1, 0.1, 0.2), vWeather);
                                }
                                
                                float light = dot(normalize(lightDirection), normalize(vNormal));
                                cloudColor *= 0.8 + 0.2 * light;
                                
                                gl_FragColor = vec4(cloudColor, 0.8);
                            }
                        `}
                        transparent
                        depthWrite={false}
                    />
                </mesh>
            ))}
        </group>
    );
}

function Rain({ weather }) {
    if (weather !== 'storm') return null;

    const rainRef = useRef();
    const positionsRef = useRef();
    const velocitiesRef = useRef();
    const cloudPositionsRef = useRef();
    const cloudRotationsRef = useRef();

    const particles = useMemo(() => {
        const count = 5000;
        const positions = new Float32Array(count * 3);
        const velocities = new Float32Array(count * 3);
        const cloudPositions = [];
        const cloudRotations = [];

        // Создаем позиции облаков для дождя
        for (let i = 0; i < 20; i++) {
            const angle = (i / 20) * Math.PI * 2;
            const radius = 100 + Math.random() * 50;
            const height = 50 + Math.random() * 30;

            cloudPositions.push([
                Math.sin(angle) * radius,
                height,
                Math.cos(angle) * radius
            ]);
            cloudRotations.push(angle);
        }

        cloudPositionsRef.current = cloudPositions;
        cloudRotationsRef.current = cloudRotations;

        for (let i = 0; i < count; i++) {
            // Выбираем случайное облако для начала дождя
            const cloudIndex = Math.floor(Math.random() * cloudPositions.length);
            const cloudPos = cloudPositions[cloudIndex];

            // Случайное смещение от центра облака
            const offsetX = (Math.random() - 0.5) * 20;
            const offsetZ = (Math.random() - 0.5) * 20;

            positions[i * 3] = cloudPos[0] + offsetX;
            positions[i * 3 + 1] = cloudPos[1];
            positions[i * 3 + 2] = cloudPos[2] + offsetZ;

            // Начальная скорость падения
            velocities[i * 3] = (Math.random() - 0.5) * 0.2;
            velocities[i * 3 + 1] = -Math.random() * 3 - 2;
            velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.2;
        }

        positionsRef.current = positions;
        velocitiesRef.current = velocities;

        return { positions, velocities };
    }, []);

    useFrame((state) => {
        if (!rainRef.current || !positionsRef.current || !velocitiesRef.current ||
            !cloudPositionsRef.current || !cloudRotationsRef.current) return;

        const positions = positionsRef.current;
        const velocities = velocitiesRef.current;
        const cloudPositions = cloudPositionsRef.current;
        const cloudRotations = cloudRotationsRef.current;

        // Обновляем позиции облаков
        const rotationSpeed = 0.001;
        for (let i = 0; i < cloudPositions.length; i++) {
            cloudRotations[i] += rotationSpeed;
            const radius = Math.sqrt(cloudPositions[i][0] * cloudPositions[i][0] +
                cloudPositions[i][2] * cloudPositions[i][2]);
            cloudPositions[i][0] = Math.sin(cloudRotations[i]) * radius;
            cloudPositions[i][2] = Math.cos(cloudRotations[i]) * radius;
        }

        for (let i = 0; i < positions.length; i += 3) {
            // Обновляем позицию капли
            positions[i] += velocities[i];
            positions[i + 1] += velocities[i + 1];
            positions[i + 2] += velocities[i + 2];

            // Если капля достигла земли или вышла за пределы сцены
            if (positions[i + 1] < -5 ||
                Math.abs(positions[i]) > 200 ||
                Math.abs(positions[i + 2]) > 200) {

                // Возвращаем каплю в случайное облако
                const cloudIndex = Math.floor(Math.random() * cloudPositions.length);
                const cloudPos = cloudPositions[cloudIndex];

                const offsetX = (Math.random() - 0.5) * 20;
                const offsetZ = (Math.random() - 0.5) * 20;

                positions[i] = cloudPos[0] + offsetX;
                positions[i + 1] = cloudPos[1];
                positions[i + 2] = cloudPos[2] + offsetZ;
            }
        }

        rainRef.current.geometry.attributes.position.needsUpdate = true;
    });

    return (
        <points ref={rainRef}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={particles.positions.length / 3}
                    array={particles.positions}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial
                size={0.3}
                color={0xffffff}
                transparent
                opacity={0.8}
                sizeAttenuation
            />
        </points>
    );
}

function WeatherSound({ weather, volume }) {
    const seagullsSound = useRef(null);
    const rainSound = useRef(null);
    const windSound = useRef(null);
    const thunderSound = useRef(null);
    const [isUserInteracted, setIsUserInteracted] = useState(false);

    // Обработчик первого взаимодействия пользователя
    useEffect(() => {
        const handleFirstInteraction = () => {
            setIsUserInteracted(true);
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
            document.removeEventListener('touchstart', handleFirstInteraction);
        };

        document.addEventListener('click', handleFirstInteraction);
        document.addEventListener('keydown', handleFirstInteraction);
        document.addEventListener('touchstart', handleFirstInteraction);

        return () => {
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
            document.removeEventListener('touchstart', handleFirstInteraction);
        };
    }, []);

    useEffect(() => {
        // Инициализация звуков
        seagullsSound.current = new Audio('/sounds/seagulls.mp3');
        rainSound.current = new Audio('/sounds/rain.mp3');
        windSound.current = new Audio('/sounds/wind.mp3');
        thunderSound.current = new Audio('/sounds/thunder.mp3');

        // Настройка зацикливания
        seagullsSound.current.loop = true;
        rainSound.current.loop = true;
        windSound.current.loop = true;

        // Начальная настройка громкости
        if (seagullsSound.current) seagullsSound.current.volume = volume * 0.1;
        if (rainSound.current) rainSound.current.volume = volume * 0.35;
        if (windSound.current) windSound.current.volume = volume * 0.35;
        if (thunderSound.current) thunderSound.current.volume = volume * 0.5;

        return () => {
            // Очистка при размонтировании
            seagullsSound.current?.pause();
            rainSound.current?.pause();
            windSound.current?.pause();
            thunderSound.current?.pause();
        };
    }, []);

    useEffect(() => {
        // Обновление громкости всех звуков
        if (seagullsSound.current) seagullsSound.current.volume = volume * 0.1;
        if (rainSound.current) rainSound.current.volume = volume * 0.35;
        if (windSound.current) windSound.current.volume = volume * 0.35;
        if (thunderSound.current) thunderSound.current.volume = volume * 0.5;
    }, [volume]);

    useEffect(() => {
        // Остановка всех звуков при смене погоды
        seagullsSound.current?.pause();
        rainSound.current?.pause();
        windSound.current?.pause();
        thunderSound.current?.pause();

        // Воспроизведение звуков в зависимости от погоды
        switch (weather) {
            case 'clear':
                if (isUserInteracted && seagullsSound.current) {
                    seagullsSound.current.currentTime = 0;
                    seagullsSound.current.play().catch(error => {
                        console.error('Error playing seagulls sound:', error);
                    });
                }
                break;
            case 'storm':
                if (isUserInteracted && rainSound.current) {
                    rainSound.current.currentTime = 0;
                    rainSound.current.play();
                }
                // Воспроизведение грома с интервалом
                const thunderInterval = setInterval(() => {
                    if (Math.random() < 0.3 && isUserInteracted) { // 30% шанс грома
                        if (thunderSound.current) {
                            thunderSound.current.currentTime = 0;
                            thunderSound.current.play();
                        }
                    }
                }, 10000); // Каждые 10 секунд
                return () => clearInterval(thunderInterval);
            case 'windy':
                if (isUserInteracted && windSound.current) {
                    windSound.current.currentTime = 0;
                    windSound.current.play();
                }
                break;
        }
    }, [weather, isUserInteracted]);

    return null;
}

function Campfire() {
    const { scene } = useGLTF('/models/костер.glb', undefined, (error) => {
        console.error('Error loading campfire model:', error);
    });
    const fireRef = useRef();

    useEffect(() => {
        scene.traverse((child) => {
            if (child.isMesh && child.material.map) {
                child.material.map.colorSpace = THREE.SRGBColorSpace;
            }
        });
    }, [scene]);

    const uniforms = useMemo(() => ({
        time: { value: 0 },
        color: { value: new THREE.Color(0xff4500) },
        intensity: { value: 1.0 }
    }), []);

    useFrame((state) => {
        uniforms.time.value = state.clock.getElapsedTime();
        uniforms.intensity.value = Math.sin(state.clock.getElapsedTime() * 2) * 0.2 + 0.8;
    });

    return (
        <group position={[30, 0.6, 60]} scale={[3, 3, 3]}>
            <primitive object={scene} />
            <mesh ref={fireRef} position={[0, -0.37, 0]}>
                <cylinderGeometry args={[0.1, 0.2, 2, 32]} />
                <shaderMaterial
                    uniforms={uniforms}
                    vertexShader={`
                        varying vec2 vUv;
                        varying float vHeight;
                        uniform float time;
                        
                        void main() {
                            vUv = uv;
                            vHeight = position.y;
                            
                            // Уменьшаем амплитуду колебаний пламени
                            float waveX = sin(position.x * 2.0 + time * 3.0) * 0.05; // Уменьшено с 0.1 до 0.05
                            float waveZ = cos(position.z * 2.0 + time * 3.0) * 0.05; // Уменьшено с 0.1 до 0.05
                            vec3 newPosition = position;
                            newPosition.x += waveX;
                            newPosition.z += waveZ;
                            
                            gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
                        }
                    `}
                    fragmentShader={`
                        varying vec2 vUv;
                        varying float vHeight;
                        uniform float time;
                        uniform vec3 color;
                        uniform float intensity;
                        
                        void main() {
                            // Создаем эффект тления
                            float heightFactor = vHeight;
                            float timeFactor = sin(time * 5.0) * 0.5 + 0.5;
                            
                            // Создаем градиент от оранжевого к красному
                            vec3 fireColor = mix(color, vec3(1.0, 0.2, 0.0), heightFactor);
                            
                            // Добавляем вариации цвета
                            float noise = sin(vUv.x * 10.0 + time * 2.0) * 0.1;
                            fireColor += vec3(noise, noise * 0.5, 0.0);
                            
                            // Усиливаем свечение
                            float glow = smoothstep(0.3, 0.7, heightFactor) * intensity;
                            fireColor *= 1.0 + glow;
                            
                            // Увеличиваем прозрачность к верху
                            float alpha = 1.0 - smoothstep(0.5, 1.0, heightFactor);
                            
                            gl_FragColor = vec4(fireColor, alpha * 0.8);
                        }
                    `}
                    transparent
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </mesh>
        </group>
    );
}

// Компонент для загрузки моделей
function ModelLoader() {
    const [models, setModels] = useState({});
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState(0);

    const loadModels = async () => {
        try {
            const modelUrls = [
                '/models/VOLCANO.glb',
                '/models/bow.glb',
                '/models/tralalelo.glb',
                '/models/костер.glb'
            ];
    
            const gltfLoader = new GLTFLoader(); // Use GLTFLoader here
            const loadedModels = {};
            let loadedCount = 0;
    
            const loadModel = (url) => {
                return new Promise((resolve, reject) => {
                    gltfLoader.load(
                        url,
                        (gltf) => {
                            loadedModels[url] = gltf;
                            loadedCount++;
                            const progressRatio = loadedCount / modelUrls.length;
                            loadingBarElement.style.width = `${progressRatio * 100}%`; // Update width based on progress
                            resolve();
                        },
                        undefined,
                        reject
                    );
                });
            };
    
            await Promise.all(modelUrls.map(loadModel));
            setModels(loadedModels);
            setLoading(false);
            loadingBarElement.classList.add('ended'); // Add class to fill the bar
    
            // Set a timeout to remove the loading bar after the transition
            setTimeout(() => {
                loadingBarElement.style.display = 'none'; // Hide the loading bar
            }, 2500); // Adjust the time as needed (1.5s for the transition + some buffer)
        } catch (error) {
            console.error('Error loading models:', error);
            setLoading(false);
        }
    };
    
    useEffect(() => {
        loadingBarElement.style.display = 'block'; // Show loading bar when loading starts
        loadModels();
    }, []);
}
export default function VolcanoScene() {
    const [weather, setWeather] = useState('clear');
    const [volume, setVolume] = useState(0.5);

    return (
        <Canvas camera={{ position: [-150, 60, 180], fov: 60 }}>
            <ModelLoader />
            <color attach="background" args={['#87ceeb']} />

            {/* Основное освещение */}
            <ambientLight
                intensity={weather === 'storm' ? 0.3 : weather === 'windy' ? 0.6 : 0.8}
                color={weather === 'storm' ? '#444444' : '#ffffff'}
            />

            {/* Основной источник света */}
            <directionalLight
                position={[10, 10, 5]}
                intensity={weather === 'storm' ? 0.5 : weather === 'windy' ? 1.0 : 1.5}
                color={weather === 'storm' ? '#444444' : '#ffffff'}
                castShadow
            />

            {/* Заполняющий свет */}
            <hemisphereLight
                intensity={weather === 'storm' ? 0.2 : weather === 'windy' ? 0.4 : 0.6}
                groundColor={weather === 'storm' ? '#222222' : '#ffffff'}
                skyColor={weather === 'storm' ? '#000000' : '#87ceeb'}
            />

            {/* Небо */}
            <Sky
                distance={450000}
                sunPosition={[0, 1, 0]}
                inclination={0.5}
                azimuth={0.25}
                turbidity={weather === 'storm' ? 20 : weather === 'windy' ? 10 : 2}
                rayleigh={weather === 'storm' ? 2 : 1}
            />

            {/* Компоненты сцены */}
            <Clouds weather={weather} />
            <Rain weather={weather} />
            <Ocean weather={weather} />
            <group renderOrder={1}>
                <VolcanoModel />
                <TralaleloModel />
                <Boat weather={weather} />
                <Campfire />
            </group>

            {/* Управление камерой */}
            <OrbitControls
                enablePan={true}
                enableZoom={true}
                enableRotate={true}
                minDistance={100}
                maxDistance={200}
                minPolarAngle={Math.PI / 6}
                maxPolarAngle={Math.PI / 2}
                minAzimuthAngle={-Math.PI / 2}
                maxAzimuthAngle={Math.PI / 2}
                target={[0, 0, 0]}
                dampingFactor={0.1}
            />

            {/* Управление погодой */}
            <WeatherControl
                onWeatherChange={setWeather}
                onVolumeChange={setVolume}
            />
            <WeatherSound weather={weather} volume={volume} />
        </Canvas>
    )
}